import { Flex, IconFont, Text } from '@byecode/ui'
import { getFileSizeAccordanceBaseUnit } from '@lighthouse/tools'
import React, { ReactNode, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { FILE_ICON_MAP } from '../../../../constants'
import { useApplicationContext } from '../../../../contexts'
import { WithStatusFile } from '../../../../types'
import { getImageFullUrlInApplication } from '../../../../utils'

const SCxContainer = styled(Flex)`
    position: relative;
    width: 100%;
    height: 149px;
    display: flex;
    align-items: center;
    font-size: var(--font-size-normal);
`

const FileIcon = styled(IconFont)<{ status?: string }>`
    font-size: 24px;
    width: 28px;
    cursor: ${({ status }) => (status === 'success' ? 'zoom-in' : 'default')};
`

const ImageThumb = styled.img<{ status?: string }>`
    width: 32px;
    cursor: ${({ status }) => (status === 'success' ? 'zoom-in' : 'default')};
`

const DeleteIcon = styled(IconFont)`
    position: absolute;
    right: 8px;
    top: 8px;
    cursor: pointer;
`

const OnlyOneFileContainer = styled(Flex)`
    padding: 8px;
`

interface UploadBoxProps {
    title: string | ReactNode
    disabled?: boolean
    innerFiles: WithStatusFile[]
    onlyOne?: boolean
    onPreview: () => void
    onRemove: (index: number) => void
}
//
export const UploadBox: React.FC<UploadBoxProps> = ({ title, disabled, innerFiles, onlyOne, onPreview, onRemove }) => {
    const { appId } = useApplicationContext()

    const onlyOneFile = useMemo(() => !!onlyOne && innerFiles.length === 1, [onlyOne, innerFiles])

    const getFileItem = useCallback(
        (file: WithStatusFile) => {
            const { type, url, name, status, size } = file
            return (
                <OnlyOneFileContainer
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={4}
                    data-field-other
                    data-field-active={false}
                    data-field-disabled={disabled}
                >
                    {type === 'image' ? (
                        url && <ImageThumb title={name} status={status} src={getImageFullUrlInApplication(appId, url)} />
                    ) : FILE_ICON_MAP[type] ? (
                        <FileIcon type={FILE_ICON_MAP[type]} status={status} size={32} />
                    ) : (
                        <FileIcon type={FILE_ICON_MAP.other} status={status} size={32} />
                    )}
                    <Text size={14}>
                        {name}（{getFileSizeAccordanceBaseUnit(size ?? 0, 'B')}
                    </Text>
                    {onlyOneFile && (
                        <DeleteIcon
                            data-field-other-icon
                            data-field-disabled={disabled}
                            type="Trash"
                            size={20}
                            onClick={() => onRemove(0)}
                        />
                    )}
                </OnlyOneFileContainer>
            )
        },
        [appId, disabled, onRemove, onlyOneFile]
    )

    const content = useMemo(
        () =>
            onlyOneFile ? (
                getFileItem(innerFiles[0])
            ) : (
                <>
                    <IconFont data-field-icon type="CloudArrowUp" size={24} />
                    <Text size={14}>{title}</Text>
                </>
            ),
        [getFileItem, innerFiles, onlyOneFile, title]
    )

    return (
        <SCxContainer
            justifyContent="center"
            alignItems="center"
            direction="column"
            gap={4}
            data-field-input
            data-field-active={false}
            data-field-disabled={!!disabled}
        >
            {content}
        </SCxContainer>
    )
}
